/**
 * Chatbot templates API client
 * (BFF: `/api/v1/store/settings/chatbot/templates`, `/templates/{key}`, `:accept-default`).
 *
 * Channel-agnostic bot texts, replacing the retired Telegram-only
 * `/api/v1/store/settings/telegram/messages/**` (now `410 Gone`). *(contract delta 2026-09-23)*
 * `getChatbotTemplateCatalog` mirrors web-content-service's frozen bot template catalogue
 * (adr-content-005 D9/D10 — one request replaces the 42 per-key reads `useChatbotTemplatesCatalog`
 * used to compose; that hook and this doc comment's earlier "no list endpoint" note are both gone).
 */
import { apiClient } from '@/shared/api';
import type {
  ChatbotChannel,
  ChatbotTemplate,
  ChatbotTemplateCatalog,
  ChatbotTemplateList,
  UpsertChatbotTemplateRequest,
} from '../model/types';

const BASE = '/api/v1/store/settings/chatbot/templates';

export interface TemplateScope {
  locale: string;
  /** Omitted/undefined = the GENERAL (every-channel) row. */
  channel?: ChatbotChannel;
}

/**
 * The catalogue for one locale/channel scope — every fixed key in catalogue order, then the
 * tenant's FAQ answers and any unlisted (retired) rows (templates doc §13.1). `channel` omitted is
 * the General view: messenger differences come from `channelVariants` and the editor's own tabs.
 */
export const getChatbotTemplateCatalog = async (
  locale: string,
  channel?: ChatbotChannel,
): Promise<ChatbotTemplateCatalog> =>
  (await apiClient.get<ChatbotTemplateCatalog>(BASE, { params: { locale, channel } })).data;

export const getChatbotTemplate = async (
  key: string,
  scope: TemplateScope,
): Promise<ChatbotTemplate> =>
  (
    await apiClient.get<ChatbotTemplate>(`${BASE}/${encodeURIComponent(key)}`, {
      params: { locale: scope.locale, channel: scope.channel },
    })
  ).data;

export const putChatbotTemplate = async (
  key: string,
  scope: TemplateScope,
  body: UpsertChatbotTemplateRequest,
): Promise<ChatbotTemplate> =>
  (
    await apiClient.put<ChatbotTemplate>(`${BASE}/${encodeURIComponent(key)}`, body, {
      params: { locale: scope.locale, channel: scope.channel },
    })
  ).data;

export const resetChatbotTemplate = async (
  key: string,
  scope: TemplateScope,
  cascade = false,
): Promise<void> => {
  await apiClient.delete(`${BASE}/${encodeURIComponent(key)}`, {
    params: { locale: scope.locale, channel: scope.channel, cascade: cascade || undefined },
  });
};

/** Accepts the platform default for every locale that has one — not per-locale (design §7.4 Q5). */
export const acceptChatbotTemplateDefault = async (key: string): Promise<ChatbotTemplateList> =>
  (await apiClient.post<ChatbotTemplateList>(`${BASE}/${encodeURIComponent(key)}:accept-default`))
    .data;
